/**
 * evidenceLine.js - the one-sentence "what did the tool look at" line printed for
 * every mapped check on a sign-off card and report (docs/launch-signoff-spec.md
 * "Evidence line", templates T1-T12).
 *
 * TWIN of proxy-server/launch/evidenceLine.js. Same logic, same strings; the pair
 * is pinned by evidenceLineParity.test.js over the shared fixtures in
 * proxy-server/launch/fixtures/evidenceLineCases.json. Change both together.
 *
 * Input is a check DEFINITION and a check RESULT in either shape:
 *   - live result (this app):  { status, matched, found, satisfied, unreadable, rows:[{url,read,ok,...}] }
 *   - persisted result (proxy): { status, matched, found, satisfied, unreadableCount, findings:[{host,path,read,ok,...}] }
 * Rows win when both exist. Rows are first put through the proxy's
 * summarizeCheckResult selection order so both shapes name the same example.
 *
 * `found` means "a value was read", NOT "the rule held". The sentence prints it as
 * a count only where that is exactly true (older run, op present/absent); otherwise
 * it uses `satisfied`, or says "result <status>".
 *
 * Never throws; always returns a non-empty string.
 */

export const SAMPLE_CAP = 3; // = proxy runStore.FINDINGS_CAP
const READ_MAX = 80;

export const MANUAL_LINE = 'Not provable from network traffic.';
export const UNMAPPED_LINE = 'No check is mapped to this criterion, so the tool validated nothing for it.';
export const NO_DEF_NO_RESULT_LINE = 'No check definition and no run result are available for this check.';
const FALLBACK_LINE = 'No evidence line is available for this check.';

/* -- minimal subset of ruleExpression.js (kept local so the proxy twin matches) -- */

export function describeSource(source, param) {
  const p = param == null ? '' : String(param);
  switch (source) {
    case 'url': return p ? `?${p}=` : 'url';
    case 'find': return p ? `?${p}= anywhere` : 'url or body';
    case 'custParams': return p ? `cust_params.${p}` : 'cust_params';
    case 'body': return p ? `body.${p}` : 'body';
    case 'response': return p ? `response.${p}` : 'response body';
    case 'validData': return p ? `validData.${p}` : 'validData';
    case 'issues': return p ? `finding ${p}` : 'validator findings';
    case 'status': return 'HTTP status';
    case 'iu': return 'ad unit';
    case 'host': return 'host';
    case 'sdk': return p ? `sdk.${p}` : 'SDK';
    case 'slot': return p ? `slot.${p}` : 'slot';
    case 'secureSignals': return p ? `secureSignals.${p}` : 'secure signals';
    case 'adTag': return p ? `adTag.${p}` : 'ad tag';
    case 'timing': return p ? `timing.${p}` : 'timing';
    default: return [source, p].filter(Boolean).join('.');
  }
}

export function describeExpectation(a) {
  if (!a) return '—';
  const val = (v) => (Array.isArray(v) ? v.join(', ') : String(v));
  switch (a.op) {
    case 'present': return 'present';
    case 'absent': return 'absent';
    case 'equals': return `= ${val(a.value)}`;
    case 'oneOf': return `one of [${val(a.values)}]`;
    case 'matches': return `~ /${a.pattern}/`;
    case 'notMatches': return `!~ /${a.pattern}/`;
    case 'gt': return `> ${val(a.value)}`;
    case 'lt': return `< ${val(a.value)}`;
    case 'range': return `between ${val(a.min)} and ${val(a.max)}`;
    case 'inBucket': return 'on a standard price-bucket grid';
    case 'validValue': return 'valid per the vendor spec';
    default: return a.op ? `op: ${a.op}` : '—';
  }
}

export const MODE_GLOSS = {
  each: 'on every request',
  all: 'on every request',
  exists: 'on at least one request',
  absent: 'on no request',
  count: 'counted across the session',
  ratio: 'as a rate across the session',
  noDuplicates: 'never repeating across the session',
  minInterval: 'spaced apart across the session',
  uniform: 'the same value across the session',
  coherent: 'agreeing across every source below',
};

/** twin of the proxy's parseHostPath (conformance/runStore.js); host includes a port */
export function parseHostPath(url) {
  if (!url || typeof url !== 'string') return { host: null, path: null };
  try {
    const u = new URL(url);
    return { host: u.host || null, path: u.pathname || null };
  } catch {
    const m = url.match(/^https?:\/\/([^/?#]+)(\/[^?#]*)?/i);
    return m ? { host: m[1] || null, path: m[2] || '/' } : { host: null, path: null };
  }
}

/* -- helpers --------------------------------------------------------------- */

const AGG_MODES = new Set(['count', 'ratio', 'noDuplicates', 'minInterval', 'uniform', 'coherent']);

function isNum(n) {
  return typeof n === 'number' && Number.isFinite(n);
}

function isObj(o) {
  return o != null && typeof o === 'object';
}

function asList(v) {
  if (Array.isArray(v)) return v.filter((x) => x != null && x !== '').map(String);
  return v != null && v !== '' ? [String(v)] : [];
}

function nounOf(types, n) {
  const base = types ? `${types} request` : 'request';
  return n === 1 ? base : `${base}s`;
}

function scopeOf(target) {
  let out = '';
  const hosts = asList(target && target.host);
  if (hosts.length) out += ` on ${hosts.join(', ')}`;
  const w = target && target.where;
  if (isObj(w)) out += ` where \`${describeSource(w.source, w.param)}\` ${describeExpectation(w)}`;
  return out;
}

/** Where the check reads from: a plain assert, else the first aggregate read. */
function primaryRead(a) {
  if (!a) return null;
  if (a.source) return { source: a.source, param: a.param };
  for (const k of ['keyParams', 'probes']) {
    if (Array.isArray(a[k]) && isObj(a[k][0]) && a[k][0].source) return a[k][0];
  }
  if (isObj(a.of) && a.of.source) return a.of;
  if (isObj(a.groupBy) && a.groupBy.source) return a.groupBy;
  return null;
}

function toSample(o, fromRow) {
  let host;
  let path;
  if (fromRow) ({ host, path } = parseHostPath(o.url));
  else { host = o.host != null ? String(o.host) : null; path = o.path != null ? String(o.path) : null; }
  let unreadable = false;
  if (o.unreadable) {
    if (fromRow) unreadable = o.unreadableKind === 'usb' || o.unreadableKind === 'stripped' ? o.unreadableKind : true;
    else unreadable = o.unreadable === 'usb' || o.unreadable === 'stripped' ? o.unreadable : true;
  }
  return {
    ok: o.ok === true,
    host,
    path,
    read: o.read != null ? String(o.read) : null,
    requestType: typeof o.requestType === 'string' ? o.requestType : '',
    unreadable,
  };
}

/**
 * Normalise a result to <= SAMPLE_CAP samples in the proxy's summarizeCheckResult
 * order (warn/fail: ok:false first; else read != null first). Live rows are
 * re-ordered here; persisted findings already are. Rows win when both exist.
 */
export function samplesOf(result) {
  const status = result.status;
  const rows = Array.isArray(result.rows) ? result.rows.filter(isObj) : [];
  if (rows.length) {
    let picked;
    if (status === 'warn' || status === 'fail') {
      const failing = rows.filter((r) => r.ok === false);
      picked = failing.length ? failing : rows;
    } else {
      const held = rows.filter((r) => r.ok === true && r.read != null); // same order as runStore
      const withRead = rows.filter((r) => r.read != null);
      picked = held.length ? held : withRead.length ? withRead : rows;
    }
    return picked.slice(0, SAMPLE_CAP).map((r) => toSample(r, true));
  }
  const findings = Array.isArray(result.findings) ? result.findings.filter(isObj) : [];
  return findings.slice(0, SAMPLE_CAP).map((f) => toSample(f, false));
}

/** The request type the samples agree on, or '' when absent / mixed. */
function commonType(samples) {
  const types = new Set(samples.map((s) => s.requestType).filter(Boolean));
  return types.size === 1 ? [...types][0] : '';
}

/** Reason an unreadable row could not be read (DE13). */
function whyOf(samples, source) {
  const first = samples.find((s) => s.unreadable);
  const kind = first ? first.unreadable : null;
  if (kind === 'stripped') return 'bodies trimmed after capture';
  if (kind === 'usb') {
    switch (source) {
      case 'body': case 'response': case 'status': case 'timing':
        return 'request-only capture — response not available';
      case 'find': return 'request-only capture — not in the URL and no request body to search';
      case 'custParams': return 'request-only capture — URL cut before this parameter';
      default: return 'request-only capture';
    }
  }
  return 'not readable in this capture';
}

function cutRead(v) {
  const s = String(v);
  return s.length > READ_MAX ? `${s.slice(0, READ_MAX)}…` : s;
}

/** Example pick (DE3): readable rows only; a bad status prefers the first non-ok one. */
function pickExample(samples, wantBad, strictBad) {
  const readable = samples.filter((s) => !s.unreadable);
  if (wantBad) {
    const bad = readable.find((s) => !s.ok);
    if (bad || strictBad) return bad || null;
  }
  return readable[0] || null;
}

function exampleClause(sample, param, more) {
  const loc = sample.host ? `${sample.host}${sample.path || ''}` : 'a request';
  let clause;
  if (sample.read == null) clause = `${loc}, no value read`;
  else if (param) clause = `${loc} with ${param}=${cutRead(sample.read)}`;
  else clause = `${loc}, read ${cutRead(sample.read)}`;
  if (isNum(more) && more >= 1) clause += ` (and ${more} more)`;
  return clause;
}

/* -- the builder ----------------------------------------------------------- */

function hasDefinition(def) {
  return isObj(def) && isObj(def.assert);
}

function missingDefLine(result, N) {
  const samples = samplesOf(result);
  const status = String(result.status || 'unknown');
  const U = isNum(result.unreadableCount) ? result.unreadableCount : (isNum(result.unreadable) ? result.unreadable : 0);
  const F = isNum(result.found) ? result.found : null;
  let out = `Looked at ${N} ${nounOf(commonType(samples), N)}. The check definition is not in this launch's suite, so what it reads is not shown; `;
  if (U > 0) out += `${U} unreadable (${whyOf(samples, undefined)}); `;
  if (F != null) out += `a value was read on ${F} of ${N}; `;
  out += `result ${status}.`;
  const bad = result.status === 'warn' || result.status === 'fail';
  const ex = pickExample(samples, bad, false);
  if (ex) out += ` Example: ${exampleClause(ex, null, 0)}.`;
  return out;
}

function build(def, result) {
  const hasResult = isObj(result);
  const N = hasResult && isNum(result.matched) ? result.matched : null;

  if (!hasDefinition(def)) {
    if (!hasResult || N === null) return NO_DEF_NO_RESULT_LINE;
    return missingDefLine(result, N);
  }

  const a = def.assert;
  const target = isObj(def.target) ? def.target : {};
  const types = asList(target.requestType).join(' or ');
  const scope = scopeOf(target);
  const read = primaryRead(a);
  const src = read ? `\`${describeSource(read.source, read.param)}\`` : '';
  const mode = typeof a.mode === 'string' ? a.mode : 'each';
  const gloss = MODE_GLOSS[mode] || MODE_GLOSS.each;

  // T8 - not run (no result, or no usable matched count)
  if (!hasResult || N === null) {
    const typePart = types ? `${types} requests` : 'requests';
    return `Would look at ${typePart}${scope}${src ? `, reading ${src}` : ''}. No run yet — run the checks and regenerate.`;
  }

  // T7 - zero matched
  if (N === 0) {
    return `Looked at 0 ${types ? `${types} requests` : 'requests'}${scope}. Nothing to grade — no matching request was captured.`;
  }

  const status = String(result.status || 'unknown');
  const looked = `Looked at ${N} ${nounOf(types, N)}${scope}.`;

  // T4 - aggregate modes: no per-row count
  if (AGG_MODES.has(mode)) {
    if (mode === 'coherent') return `${looked} Checked the sources named in the check for agreement; result ${status}.`;
    return `${looked} Checked ${src || 'the values the check names'} ${gloss}; result ${status}.`;
  }

  const samples = samplesOf(result);
  const U = isNum(result.unreadableCount) ? result.unreadableCount : (isNum(result.unreadable) ? result.unreadable : 0);
  const why = whyOf(samples, read ? read.source : undefined);

  // T6 - every matched row unreadable
  if (U > 0 && U >= N) {
    const srcPart = src ? `Read ${src}, but all` : 'All';
    return `${looked} ${srcPart} ${N} matched ${N === 1 ? 'request was' : 'requests were'} unreadable (${why}); not graded.`;
  }

  const R = N - U;
  const S = isNum(result.satisfied) ? result.satisfied : null;
  const F = isNum(result.found) ? result.found : null;
  const prefix = U > 0 ? `${N} matched, ${U} unreadable (${why}); ` : '';
  const srcOrValue = src || 'the value';
  const exp = describeExpectation(a);
  const bad = status === 'warn' || status === 'fail';

  let lead;
  let count;
  let absence = false;
  if (mode === 'absent') {
    absence = true;
    lead = Array.isArray(a.allow)
      ? `No request may carry keys on ${srcOrValue} beyond the allowed list`
      : `No request may have ${srcOrValue} ${exp}`;
    count = S != null ? `absent on ${S} of ${R} readable` : `result ${status}`;
  } else if (a.op === 'absent') {
    absence = true;
    lead = `Read ${srcOrValue}, which must be absent ${gloss}`;
    if (S != null) count = `absent on ${S} of ${R} readable`;
    else if (F != null) count = `absent on ${N - F} of ${N}`;
    else count = `result ${status}`;
  } else {
    lead = `Read ${srcOrValue}, expected ${exp} ${gloss}`;
    if (S != null) count = `held on ${S} of ${R} readable`;
    else if (a.op === 'present' && F != null) count = `held on ${F} of ${N}`;
    else count = `result ${status}`;
  }

  let out = `${looked} ${lead}; ${prefix}${count}.`;

  // Example clause
  const param = a.param ? String(a.param) : '';
  if (absence) {
    // Offending example only for a warn/fail with at least one readable offender.
    if (bad && (S == null || R - S >= 1)) {
      const ex = pickExample(samples, true, true);
      if (ex) {
        const more = S != null ? R - S - 1 : null;
        out += ` Offending example: ${exampleClause(ex, param, more)}.`;
      }
    }
  } else {
    const ex = pickExample(samples, bad, false);
    if (ex) {
      let more = null;
      if (S != null) more = ex.ok ? S - 1 : R - S - 1;
      out += ` Example: ${exampleClause(ex, param, more)}.`;
    }
  }
  return out;
}

/**
 * evidenceLine(def, result) -> string
 * def: the Check definition or null. result: the check result (live or
 * persisted shape) or null. Never throws; never empty.
 */
export function evidenceLine(def, result) {
  try {
    const s = build(def, result);
    return typeof s === 'string' && s ? s : FALLBACK_LINE;
  } catch {
    return FALLBACK_LINE;
  }
}

export const manualEvidenceLine = () => MANUAL_LINE;
export const unmappedEvidenceLine = () => UNMAPPED_LINE;
